import { byDate, byNumber, byText, thenBy } from '../list-sort';

import type { ID } from '../../types';
import type {
  WorkoutDifficulty,
  WorkoutTemplate,
} from '../../types/domain/workout.types';
import type { SortCatalog } from '../list-sort';

/**
 * Workout List Model
 *
 * The Workouts list's filtering and row shaping, shared by the web page and the
 * mobile screen so both narrow the same list the same way. The list is fetched
 * whole (see `workout.store`), so every filter here runs on the client.
 */

// =============================================================================
// TYPES
// =============================================================================

/** Workout status filter options. */
export type WorkoutStatusFilter = 'active' | 'all' | 'completed';

/** Workout filter state for search and filtering. */
export interface WorkoutFilterState {
  /** Status filter: active, all, or completed workouts */
  status: WorkoutStatusFilter;
  /** Difficulty filter (optional) */
  difficulty?: WorkoutDifficulty;
  /** Search query for name/description */
  search?: string;
  /** Filter by plan name keywords (comma-separated, substring match) */
  planSearch?: string;
  /**
   * Filter by plan, exactly: a workout passes when its plan is one of these.
   * The picker's filter — unlike `planSearch`, "Push" does not also match
   * "Push Pull Legs", and a plan name may contain a comma.
   */
  planIds?: ID[];
}

/** Exercise item for the expanded workout card. */
export interface WorkoutExerciseItem {
  id: ID;
  name: string;
  sets: number;
  previousSets: {
    reps: number | null;
    weight: number | null;
    duration: number | null;
    distance: number | null;
  }[];
}

/** A plan that at least one listed workout belongs to. */
export interface WorkoutPlanOption {
  id: ID;
  name: string;
  /** How many of the listed workouts belong to it. */
  count: number;
}

// =============================================================================
// FUNCTIONS
// =============================================================================

/**
 * Filter workouts based on the current filter state. Every set filter must
 * pass; a workout with no plan fails any plan filter.
 */
export function filterWorkouts(
  workouts: WorkoutTemplate[],
  filters: WorkoutFilterState
): WorkoutTemplate[] {
  let filtered = [...workouts];

  // Filter by difficulty
  if (filters.difficulty) {
    filtered = filtered.filter(
      (workout) => workout.difficulty === filters.difficulty
    );
  }

  // Filter by search
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(
      (workout) =>
        workout.name.toLowerCase().includes(searchLower) ||
        workout.description?.toLowerCase()?.includes(searchLower)
    );
  }

  // Filter by plan name keywords (comma-separated)
  if (filters.planSearch) {
    const keywords = filters.planSearch
      .split(',')
      .map((k) => k.trim().toLowerCase())
      .filter((k) => k.length > 0);

    if (keywords.length > 0) {
      filtered = filtered.filter((workout) => {
        if (!workout.plan?.id) return false;
        const planName = workout.plan.name.toLowerCase();
        return keywords.some((keyword) => planName.includes(keyword));
      });
    }
  }

  // Filter by exact plan
  if (filters.planIds && filters.planIds.length > 0) {
    const planIds = new Set(filters.planIds.map((id) => id.toString()));
    filtered = filtered.filter(
      (workout) =>
        workout.plan?.id != null && planIds.has(workout.plan.id.toString())
    );
  }

  return filtered;
}

/**
 * Transform a workout template's exercises to exercise items, pairing each
 * with its sets from the previous session.
 */
export function mapExercisesToItems(
  workout: WorkoutTemplate
): WorkoutExerciseItem[] {
  const prevExercises = workout.previous?.exercises ?? [];
  return workout.exercises.map((exercise) => ({
    id: exercise.id,
    name: exercise.name,
    sets: exercise.sets,
    previousSets:
      prevExercises
        .filter(
          (prevExercise) =>
            prevExercise.id.toString() === exercise.id.toString()
        )
        ?.at(0)?.sets ?? [],
  }));
}

/**
 * The plans the listed workouts belong to, each with its workout count, sorted
 * by name — the options of a plan picker. Counts are over the whole list, not
 * the filtered one, so they hold still while filters change.
 */
export function listWorkoutPlans(
  workouts: WorkoutTemplate[]
): WorkoutPlanOption[] {
  const plans = new Map<string, WorkoutPlanOption>();

  for (const workout of workouts) {
    if (workout.plan?.id == null) continue;
    const key = workout.plan.id.toString();
    const plan = plans.get(key);
    if (plan) {
      plan.count += 1;
    } else {
      plans.set(key, {
        id: workout.plan.id,
        name: workout.plan.name,
        count: 1,
      });
    }
  }

  return [...plans.values()].sort((a, b) => a.name.localeCompare(b.name));
}

// =============================================================================
// SORTING
// =============================================================================

export type WorkoutSortOrder =
  'newest' | 'oldest' | 'name' | 'recent' | 'mostDone';

const byWorkoutName = byText<WorkoutTemplate>((workout) => workout.name);

/** The Workouts list's sorts. A tie falls back to the name. */
export const WORKOUT_SORTS: SortCatalog<WorkoutTemplate, WorkoutSortOrder> = {
  newest: {
    label: 'Newest',
    compare: thenBy(
      byDate((workout) => workout.created_at, 'desc'),
      byWorkoutName
    ),
  },
  oldest: {
    label: 'Oldest',
    compare: thenBy(
      byDate((workout) => workout.created_at, 'asc'),
      byWorkoutName
    ),
  },
  name: { label: 'Name A–Z', compare: byWorkoutName },
  recent: {
    label: 'Recently done',
    // Never done goes last.
    compare: thenBy(
      byDate((workout) => workout.previous?.date, 'desc'),
      byWorkoutName
    ),
  },
  mostDone: {
    label: 'Most done',
    compare: thenBy(
      byNumber((workout) => workout.completed, 'desc'),
      byWorkoutName
    ),
  },
};

export const DEFAULT_WORKOUT_SORT: WorkoutSortOrder = 'newest';
